/**
 * Stok (back office): kiraan fizikal dan pelarasan.
 *
 * Sebelum ini stok hanya boleh ditetapkan semasa import CSV. Selepas itu tiada cara membetulkannya,
 * jadi jualan barang yang tidak pernah dimasukkan stok meninggalkan stok negatif selama-lamanya.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatMYR } from '@72on/domain';
import {
  adjustStock,
  countStock,
  createSupplier,
  listReceipts,
  listStock,
  listSuppliers,
  receiveStock,
  type ReceiptRow,
  type StockRow,
  type SupplierRow,
} from '../offline/admin.js';
import { API_BASE } from '../config.js';

interface Props {
  token: string;
  outletId: string | undefined;
}

export function StockPanel({ token, outletId }: Props): React.ReactElement {
  const [rows, setRows] = useState<StockRow[] | null>(null);
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [onlyNegative, setOnlyNegative] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);

  const [adjustFor, setAdjustFor] = useState<StockRow | null>(null);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  /** Terimaan barang daripada pembekal. */
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierRow[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [refNo, setRefNo] = useState('');
  const [receiptLines, setReceiptLines] = useState<
    Array<{ variantId: string; qty: string; cost: string }>
  >([{ variantId: '', qty: '', cost: '' }]);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierPhone, setNewSupplierPhone] = useState('');
  const [receipts, setReceipts] = useState<ReceiptRow[]>([]);

  const load = useCallback(async (): Promise<void> => {
    if (outletId === undefined) return;
    setBusy(true);
    try {
      const [stock, supplierRows, receiptRows] = await Promise.all([
        listStock(API_BASE, token, outletId),
        listSuppliers(API_BASE, token).catch(() => [] as SupplierRow[]),
        listReceipts(API_BASE, token).catch(() => [] as ReceiptRow[]),
      ]);
      setRows(stock);
      setSuppliers(supplierRows);
      setReceipts(receiptRows);
    } catch (error) {
      // Jangan biarkan panel "Memuatkan…" selama-lamanya: ralat mesti kelihatan bersama jadual kosong.
      setRows([]);
      setMessage({ text: error instanceof Error ? error.message : 'gagal memuatkan stok', error: true });
    } finally {
      setBusy(false);
    }
  }, [token, outletId]);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(() => {
    if (rows === null) return [];
    return onlyNegative ? rows.filter((row) => Number(row.qty) < 0) : rows;
  }, [rows, onlyNegative]);

  async function saveCounts(): Promise<void> {
    if (outletId === undefined) return;
    const filled = Object.entries(counts).filter(([, value]) => value.trim() !== '');
    if (filled.length === 0) {
      setMessage({ text: 'Masukkan kiraan untuk sekurang-kurangnya satu barang.', error: true });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const result = await countStock(
        API_BASE,
        token,
        outletId,
        filled.map(([variantId, value]) => ({ variantId, countedQty: value.trim().replace(',', '.') })),
        note.trim() === '' ? undefined : note.trim(),
      );
      setCounts({});
      setNote('');
      await load();
      setMessage({
        text: `Kiraan disimpan: ${result.changed} barang dilaraskan mengikut kiraan fizikal.`,
        error: false,
      });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal menyimpan kiraan', error: true });
    } finally {
      setBusy(false);
    }
  }

  async function saveAdjustment(): Promise<void> {
    if (outletId === undefined || adjustFor === null) return;
    if (adjustReason.trim().length < 3) {
      setMessage({ text: 'Sebab pelarasan diperlukan (sekurang-kurangnya 3 aksara).', error: true });
      return;
    }
    setBusy(true);
    try {
      await adjustStock(
        API_BASE,
        token,
        outletId,
        adjustFor.variant_id,
        adjustDelta.trim().replace(',', '.'),
        adjustReason.trim(),
      );
      setAdjustFor(null);
      setAdjustDelta('');
      setAdjustReason('');
      await load();
      setMessage({ text: 'Stok dilaras.', error: false });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal melaras stok', error: true });
    } finally {
      setBusy(false);
    }
  }

  async function addSupplier(): Promise<void> {
    if (newSupplierName.trim().length < 2) {
      setMessage({ text: 'Nama pembekal terlalu pendek.', error: true });
      return;
    }
    setBusy(true);
    try {
      const supplier = await createSupplier(API_BASE, token, {
        name: newSupplierName.trim(),
        ...(newSupplierPhone.trim() === '' ? {} : { phone: newSupplierPhone.trim() }),
      });
      setSuppliers((current) => [...current, { ...supplier, receipts: 0 }]);
      setSupplierId(supplier.id);
      setNewSupplierName('');
      setNewSupplierPhone('');
      setMessage({ text: `Pembekal "${supplier.name}" ditambah.`, error: false });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal menambah pembekal', error: true });
    } finally {
      setBusy(false);
    }
  }

  async function saveReceipt(): Promise<void> {
    if (outletId === undefined) return;
    const lines = receiptLines
      .filter((line) => line.variantId !== '' && line.qty.trim() !== '')
      .map((line) => ({
        variantId: line.variantId,
        qty: line.qty.trim().replace(',', '.'),
        unitCostSen: Math.round(Number(line.cost.replace(',', '.') || '0') * 100),
      }));

    if (lines.length === 0) {
      setMessage({ text: 'Tambah sekurang-kurangnya satu baris barang.', error: true });
      return;
    }

    setBusy(true);
    setMessage(null);
    try {
      const result = await receiveStock(API_BASE, token, {
        outletId,
        ...(supplierId === '' ? {} : { supplierId }),
        ...(refNo.trim() === '' ? {} : { refNo }),
        lines,
      });
      setReceiveOpen(false);
      setReceiptLines([{ variantId: '', qty: '', cost: '' }]);
      setRefNo('');
      await load();
      setMessage({
        text: `Terimaan disimpan: ${result.lines} baris, kos RM${(result.total_cost_sen / 100).toFixed(2)}. Stok dan kos purata dikemas kini.`,
        error: false,
      });
    } catch (error) {
      setMessage({ text: error instanceof Error ? error.message : 'gagal menyimpan terimaan', error: true });
    } finally {
      setBusy(false);
    }
  }

  if (outletId === undefined) {
    return (
      <section className="panel-stock">
        <h2>Stok</h2>
        <p className="muted">Tiada cawangan untuk dipaparkan.</p>
      </section>
    );
  }

  const negativeCount = rows?.filter((row) => Number(row.qty) < 0).length ?? 0;

  return (
    <section className="panel-stock">
      <h2>Stok</h2>
      <p className="muted">
        Kiraan fizikal menetapkan stok kepada bilangan sebenar dan merekod beza sebagai gerakan
        stok — bukan menulis semula angka secara senyap. Setiap pelarasan memerlukan sebab.
      </p>

      {negativeCount > 0 && (
        <p className="error">
          {negativeCount} barang mempunyai stok negatif. Ini bermakna barang itu terjual tanpa stok
          dimasukkan. Kira dan simpan untuk membetulkannya.
        </p>
      )}

      {message !== null && <p className={message.error ? 'error' : 'muted'}>{message.text}</p>}

      <div className="printer-row">
        <label className="inline">
          <input
            type="checkbox"
            checked={onlyNegative}
            onChange={(event) => setOnlyNegative(event.target.checked)}
          />
          Tunjuk hanya stok negatif
        </label>
        <label>
          Nota kiraan (pilihan)
          <input value={note} onChange={(event) => setNote(event.target.value)} maxLength={200} />
        </label>
        <button type="button" disabled={busy} onClick={() => void load()}>
          {busy ? 'Memuatkan…' : 'Muat semula'}
        </button>
        <button type="button" disabled={busy} onClick={() => void saveCounts()}>
          Simpan kiraan
        </button>
        <button type="button" disabled={busy} onClick={() => setReceiveOpen(true)}>
          Terima barang
        </button>
      </div>

      {rows === null ? (
        <p className="muted">Memuatkan stok…</p>
      ) : visible.length === 0 ? (
        <p className="muted">{onlyNegative ? 'Tiada stok negatif.' : 'Tiada barang.'}</p>
      ) : (
        <table className="table table-stock">
          <thead>
            <tr>
              <th>Barang</th>
              <th>Stok sistem</th>
              <th>Kiraan</th>
              <th>Beza</th>
              <th>Tindakan</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const typed = counts[row.variant_id] ?? '';
              const counted = typed.trim() === '' ? null : Number(typed.replace(',', '.'));
              const delta = counted === null ? null : counted - Number(row.qty);
              return (
                <tr key={row.variant_id}>
                  <td>
                    {row.product_name}
                    {row.variant_name !== null && row.variant_name !== '' ? ` · ${row.variant_name}` : ''}
                    <p className="muted">
                      {row.sku !== null && row.sku !== '' ? `${row.sku} · ` : ''}
                      RM{formatMYR(row.price_sen)} · kos RM{formatMYR(row.cost_sen)}
                    </p>
                  </td>
                  <td className={Number(row.qty) < 0 ? 'error' : undefined}>
                    {Number(row.qty)} {row.unit}
                  </td>
                  <td>
                    <input
                      inputMode="decimal"
                      value={typed}
                      placeholder="—"
                      onChange={(event) =>
                        setCounts((current) => ({ ...current, [row.variant_id]: event.target.value }))
                      }
                    />
                  </td>
                  <td>
                    {delta === null
                      ? '—'
                      : delta === 0
                        ? '0'
                        : `${delta > 0 ? '+' : ''}${Number(delta.toFixed(3))}`}
                  </td>
                  <td>
                    <button type="button" className="ghost" onClick={() => setAdjustFor(row)}>
                      Pelarasan
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {receiveOpen && (
        <div className="modal">
          <div className="sheet">
            <h3>Terima barang</h3>
            <p className="muted">
              Stok bertambah dan kos barang dikemas kini secara purata berwajaran — jadi untung
              dikira dengan kos sebenar, bukan kos lama.
            </p>

            <div className="printer-row">
              <label>
                Pembekal
                <select value={supplierId} onChange={(event) => setSupplierId(event.target.value)}>
                  <option value="">— tiada —</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                No. invois (pilihan)
                <input value={refNo} onChange={(event) => setRefNo(event.target.value)} maxLength={60} />
              </label>
            </div>

            <div className="printer-row">
              <label>
                Pembekal baharu
                <input
                  value={newSupplierName}
                  onChange={(event) => setNewSupplierName(event.target.value)}
                  placeholder="Nama"
                />
              </label>
              <label>
                Telefon
                <input
                  value={newSupplierPhone}
                  onChange={(event) => setNewSupplierPhone(event.target.value)}
                  placeholder="03-1234 5678"
                />
              </label>
              <button type="button" className="ghost" disabled={busy} onClick={() => void addSupplier()}>
                Tambah pembekal
              </button>
            </div>

            {receiptLines.map((line, index) => (
              <div className="printer-row" key={index}>
                <label>
                  Barang
                  <select
                    value={line.variantId}
                    onChange={(event) =>
                      setReceiptLines((current) =>
                        current.map((item, at) =>
                          at === index ? { ...item, variantId: event.target.value } : item,
                        ),
                      )
                    }
                  >
                    <option value="">— pilih —</option>
                    {(rows ?? []).map((row) => (
                      <option key={row.variant_id} value={row.variant_id}>
                        {row.product_name}
                        {row.variant_name !== null && row.variant_name !== '' ? ` · ${row.variant_name}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Kuantiti
                  <input
                    inputMode="decimal"
                    value={line.qty}
                    onChange={(event) =>
                      setReceiptLines((current) =>
                        current.map((item, at) =>
                          at === index ? { ...item, qty: event.target.value } : item,
                        ),
                      )
                    }
                  />
                </label>
                <label>
                  Harga belian seunit (RM)
                  <input
                    inputMode="decimal"
                    value={line.cost}
                    onChange={(event) =>
                      setReceiptLines((current) =>
                        current.map((item, at) =>
                          at === index ? { ...item, cost: event.target.value } : item,
                        ),
                      )
                    }
                  />
                </label>
              </div>
            ))}

            <div className="actions">
              <button
                type="button"
                className="ghost"
                onClick={() =>
                  setReceiptLines((current) => [...current, { variantId: '', qty: '', cost: '' }])
                }
              >
                Tambah baris
              </button>
            </div>

            <div className="actions">
              <button type="button" className="ghost" onClick={() => setReceiveOpen(false)}>
                Tutup
              </button>
              <button type="button" disabled={busy} onClick={() => void saveReceipt()}>
                Simpan terimaan
              </button>
            </div>
          </div>
        </div>
      )}

      {receipts.length > 0 && (
        <div>
          <h3>Terimaan terkini</h3>
          <table className="table">
            <thead>
              <tr>
                <th>Tarikh</th>
                <th>Pembekal</th>
                <th>Invois</th>
                <th>Baris</th>
                <th>Jumlah kos</th>
              </tr>
            </thead>
            <tbody>
              {receipts.slice(0, 10).map((receipt) => (
                <tr key={receipt.id}>
                  <td>
                    {new Date(receipt.received_at).toLocaleDateString('ms-MY', {
                      timeZone: 'Asia/Kuala_Lumpur',
                    })}
                  </td>
                  <td>{receipt.supplier_name ?? '—'}</td>
                  <td>{receipt.ref_no ?? '—'}</td>
                  <td>{receipt.lines}</td>
                  <td>RM{formatMYR(receipt.total_cost_sen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {adjustFor !== null && (
        <div className="modal">
          <div className="sheet">
            <h3>Pelarasan stok</h3>
            <p className="muted">
              {adjustFor.product_name}
              {adjustFor.variant_name !== null ? ` · ${adjustFor.variant_name}` : ''} — stok sekarang{' '}
              {Number(adjustFor.qty)} {adjustFor.unit}.
            </p>
            <label>
              Perubahan (contoh 10 untuk masuk, -2 untuk rosak)
              <input
                inputMode="decimal"
                value={adjustDelta}
                onChange={(event) => setAdjustDelta(event.target.value)}
                placeholder="10"
              />
            </label>
            <label>
              Sebab
              <input
                value={adjustReason}
                onChange={(event) => setAdjustReason(event.target.value)}
                placeholder="Barang masuk tanpa pembelian"
              />
            </label>
            <div className="actions">
              <button type="button" className="ghost" onClick={() => setAdjustFor(null)}>
                Tutup
              </button>
              <button type="button" disabled={busy} onClick={() => void saveAdjustment()}>
                Simpan pelarasan
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
